import type { ReactElement } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { ErrorNote } from "../components/ui";

// The server redirects to /?auth_error=… when sign-in is refused (Auth/DarkroomAuth.cs). Each
// value gets its own copy: an error says what happened and what to do next, in the interface's
// voice, and never apologises (IMPLEMENTATION_PLAN.md §11.2).
// Widened deliberately: the query string is whatever arrives, so a lookup can miss.
const AUTH_ERRORS: Record<string, { title: string; detail: string } | undefined> = {
  not_allowed: {
    title: "This Google account is not on the Darkroom allowlist.",
    detail:
      "The allowlist is set by whoever runs this instance. Ask them to add your work address, then sign in again.",
  },
  email_not_verified: {
    title: "Google has not verified the email address on that account.",
    detail: "Verify the address with Google, then come back and sign in again.",
  },
  no_email: {
    title: "That Google account did not share an email address.",
    detail: "Darkroom identifies you by email. Sign in with an account that has one.",
  },
  account_disabled: {
    title: "This account has been deactivated.",
    detail: "An administrator can restore it.",
  },
  sign_in_failed: {
    title: "Sign-in did not finish.",
    detail: "The exchange with Google broke off part way through. Starting again usually clears it.",
  },
};

const UNKNOWN_FAILURE = {
  title: "Sign-in did not finish.",
  detail: "Try again, and tell whoever runs this instance if it keeps happening.",
};

export function SignIn(): ReactElement {
  const location = useLocation();
  const [search] = useSearchParams();

  const reason = search.get("auth_error");
  const failure = reason ? (AUTH_ERRORS[reason] ?? UNKNOWN_FAILURE) : null;

  return (
    <div className="signin">
      <div className="signin-card">
        <span className="lamp" aria-hidden />
        <h1 className="signin-title">Darkroom</h1>
        <p className="lede">Generate, edit and iterate on images across four providers.</p>

        {failure && (
          // The card centres its text; a message that runs to two lines reads better ranged left.
          <div style={{ width: "100%", textAlign: "left" }}>
            <ErrorNote title={failure.title} detail={failure.detail} />
          </div>
        )}

        <button
          type="button"
          className="btn primary"
          onClick={() => {
            // A full navigation, not fetch: the OIDC challenge is a redirect the browser must own.
            window.location.href = api.signInUrl(location.pathname + location.search);
          }}
        >
          Continue with Google
        </button>
      </div>
    </div>
  );
}
